import type { HeadPiece } from './human-head.js';

/**
 * Hair under headgear. A hat or helm neither deletes the hairstyle nor lets it poke through: hair is pressed in under the piece and the
 * triangles that end up inside it are dropped, so only what the headgear leaves out is drawn (a tail through the back of a cap, locks
 * below a bascinet's guard, a fringe in a hood's opening).
 *
 * A fit is a list of parts in skull space. Each has `inner`, the signed distance to the room inside the piece (negative within), and `cover`,
 * from 0 where the piece leaves the hair alone to 1 where it holds it: the ramp lets hair run out from under a rim instead of ending on a
 * cut edge. Hair that stands out of `inner` where it is covered is pulled onto the inside of the piece.
 */
export type SkullField = (x: number, y: number, z: number) => number;
export interface HairFitPart { inner: SkullField; cover: SkullField }
export interface HairFit { key: string; parts: HairFitPart[] }

/** Past this cover a vertex is held fully under the piece. */
const HELD = .999;
/** A held vertex this close to the inside of the piece is hidden by it. */
const HIDDEN = .002;
/** A piece of hair cut from the rest must end against the headwear: if its cut edge stands this far clear of it, the piece is left floating and is dropped. */
const FLOATING = .015;

function gradient(f: SkullField, x: number, y: number, z: number, e = 3e-4): [number, number, number] {
  const gx = f(x + e, y, z) - f(x - e, y, z), gy = f(x, y + e, z) - f(x, y - e, z), gz = f(x, y, z + e) - f(x, y, z - e), l = Math.hypot(gx, gy, gz) || 1;
  return [gx / l, gy / l, gz / l];
}

/**
 * The pieces of a hairstyle pressed under a piece of headwear, with the triangles it hides removed. Hair that the cut leaves with no end
 * against the headwear (the tip of a lock that hung inside a hood) is dropped with it. Pieces left with nothing are dropped.
 */
export function fitHair(pieces: HeadPiece[], fit: HairFit): HeadPiece[] {
  return pieces.flatMap(piece => {
    const count = piece.positions.length / 3, positions = piece.positions.slice(), hidden = new Uint8Array(count), gap = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      let x = positions[i * 3], y = positions[i * 3 + 1], z = positions[i * 3 + 2];
      for (const { inner, cover } of fit.parts) {
        let held = cover(x, y, z);
        if (held <= 0) continue;
        // A first step carries a vertex in the ramp part of the way under the rim. If that lands it where the piece holds hair fully,
        // it is finished onto the surface: the field is only roughly a distance, so a held vertex steps in over a few passes.
        for (let pass = 0; pass < 5; pass++) {
          const s = inner(x, y, z);
          if (s <= 0) break;
          const g = gradient(inner, x, y, z);
          x -= g[0] * s * held; y -= g[1] * s * held; z -= g[2] * s * held;
          held = cover(x, y, z);
          if (held < HELD) break;
        }
        const left = inner(x, y, z);
        if (cover(x, y, z) >= HELD && left < HIDDEN) hidden[i] = 1;
        gap[i] = Math.max(gap[i], left);
      }
      positions[i * 3] = x; positions[i * 3 + 1] = y; positions[i * 3 + 2] = z;
    }
    // Triangles wholly under the headwear go; the rest are grouped into connected pieces, each judged by where it was cut.
    const parent = Int32Array.from({ length: count }, (_, i) => i), find = (i: number): number => { while (parent[i] !== i) i = parent[i] = parent[parent[i]]; return i; };
    const kept: number[] = [], cut = new Uint8Array(count);
    for (let t = 0; t < piece.indices.length; t += 3) {
      const a = piece.indices[t], b = piece.indices[t + 1], c = piece.indices[t + 2];
      if (hidden[a] && hidden[b] && hidden[c]) { cut[a] = cut[b] = cut[c] = 1; continue; }
      kept.push(a, b, c); parent[find(a)] = find(b); parent[find(b)] = find(c);
    }
    const worst = new Map<number, number>();
    for (const index of kept) if (cut[index]) worst.set(find(index), Math.max(worst.get(find(index)) ?? 0, gap[index]));
    const indices: number[] = [];
    for (let t = 0; t < kept.length; t += 3) if ((worst.get(find(kept[t])) ?? 0) <= FLOATING) indices.push(kept[t], kept[t + 1], kept[t + 2]);
    return indices.length ? [{ ...piece, positions, indices: new Uint32Array(indices) }] : [];
  });
}
